import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Search, RefreshCw, Link2, ChevronDown, ChevronRight } from 'lucide-react';

const MouldParts = () => {
  const { api, isAdmin, hasRole } = useAuth();
  const [data, setData] = useState([]);
  const [moulds, setMoulds] = useState([]);
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedMouldId, setSelectedMouldId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [partLines, setPartLines] = useState([]);
  const [expandedMoulds, setExpandedMoulds] = useState({});
  const [isNewMould, setIsNewMould] = useState(false);

  const canEdit = isAdmin() || hasRole(['ADMIN', 'PRODUCTION']);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [mouldPartsRes, mouldsRes, partsRes] = await Promise.all([
        api.get('/mould-parts'),
        api.get('/moulds'),
        api.get('/parts')
      ]);
      setData(mouldPartsRes.data);
      setMoulds(mouldsRes.data);
      setParts(partsRes.data);
      // Auto-expand all moulds
      const expanded = {};
      const mouldIds = [...new Set(mouldPartsRes.data.map(mp => mp.mould_id))];
      mouldIds.forEach(id => { expanded[id] = true; });
      setExpandedMoulds(expanded);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getMouldName = (mouldId) => {
    const mould = moulds.find(m => m.id === mouldId);
    return mould ? `${mould.code} - ${mould.name}` : `ID: ${mouldId}`;
  };

  const getPartName = (partId) => {
    const part = parts.find(p => p.id === partId);
    return part ? `${part.part_no} - ${part.name}` : `ID: ${partId}`;
  };

  // Group data by mould
  const groupedData = React.useMemo(() => {
    const groups = {};
    data.forEach(item => {
      if (!groups[item.mould_id]) {
        groups[item.mould_id] = [];
      }
      groups[item.mould_id].push(item);
    });
    return groups;
  }, [data]);

  const filteredMouldIds = React.useMemo(() => {
    const mouldIds = Object.keys(groupedData).map(Number);
    if (!searchTerm) return mouldIds;
    const term = searchTerm.toLowerCase();
    return mouldIds.filter(mouldId => {
      const mouldName = getMouldName(mouldId).toLowerCase();
      if (mouldName.includes(term)) return true;
      return groupedData[mouldId].some(item =>
        getPartName(item.part_id).toLowerCase().includes(term)
      );
    });
  }, [groupedData, searchTerm, moulds, parts]);

  const toggleMould = (mouldId) => {
    setExpandedMoulds(prev => ({ ...prev, [mouldId]: !prev[mouldId] }));
  };

  // Open dialog for a mould - shows all its existing parts + ability to add more
  const handleEditMould = (mouldId) => {
    setSelectedMouldId(mouldId.toString());
    setIsNewMould(false);
    const mouldParts = groupedData[mouldId] || [];
    setPartLines(mouldParts.map(mp => ({
      id: mp.id,
      part_id: mp.part_id.toString(),
      total_cavities: (mp.total_cavities ? mp.total_cavities.toString() : ''),
      remarks: mp.remarks || '',
      is_active: mp.is_active,
      _existing: true,
    })));
    setIsDialogOpen(true);
  };

  // Open dialog for new mould mapping
  const handleAddNew = () => {
    setSelectedMouldId('');
    setIsNewMould(true);
    setPartLines([{ part_id: '', total_cavities: '', remarks: '', is_active: true, _existing: false }]);
    setIsDialogOpen(true);
  };

  const handleAddPartLine = () => {
    setPartLines([...partLines, { part_id: '', total_cavities: '', remarks: '', is_active: true, _existing: false }]);
  };

  const handlePartLineChange = (index, field, value) => {
    const updated = [...partLines];
    updated[index] = { ...updated[index], [field]: value };
    setPartLines(updated);
  };

  const handleRemovePartLine = (index) => {
    const line = partLines[index];
    if (line._existing && line.id) {
      // Mark for deletion
      const updated = [...partLines];
      updated[index] = { ...updated[index], _deleted: true };
      setPartLines(updated);
    } else {
      setPartLines(partLines.filter((_, i) => i !== index));
    }
  };

  const handleUndoRemove = (index) => {
    const updated = [...partLines];
    updated[index] = { ...updated[index], _deleted: false };
    setPartLines(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedMouldId) {
      toast.error('Please select a mould');
      return;
    }
    setIsSubmitting(true);
    try {
      const mouldId = parseInt(selectedMouldId);
      let created = 0, updated = 0, deleted = 0;

      for (const line of partLines) {
        if (line._deleted && line._existing && line.id) {
          // Delete removed existing mappings
          await api.delete(`/mould-parts/${line.id}`);
          deleted++;
        } else if (line._deleted) {
          continue;
        } else if (line._existing && line.id) {
          // Update existing
          await api.put(`/mould-parts/${line.id}`, {
            mould_id: mouldId,
            part_id: parseInt(line.part_id),
            total_cavities: parseInt(line.total_cavities),
            remarks: line.remarks || null,
            is_active: line.is_active !== false,
          });
          updated++;
        } else if (line.part_id && line.total_cavities) {
          // Create new
          await api.post('/mould-parts', {
            mould_id: mouldId,
            part_id: parseInt(line.part_id),
            total_cavities: parseInt(line.total_cavities),
            remarks: line.remarks || null,
            is_active: true,
          });
          created++;
        }
      }

      const msgs = [];
      if (created) msgs.push(`${created} added`);
      if (updated) msgs.push(`${updated} updated`);
      if (deleted) msgs.push(`${deleted} removed`);
      toast.success(msgs.length ? msgs.join(', ') : 'No changes');
      setIsDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error((error.response && error.response.data && error.response.data.detail) || 'Operation failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteMapping = async (item) => {
    if (!window.confirm('Delete this mapping?')) return;
    try {
      await api.delete(`/mould-parts/${item.id}`);
      toast.success('Mapping deleted');
      fetchData();
    } catch (error) {
      toast.error((error.response && error.response.data && error.response.data.detail) || 'Delete failed');
    }
  };

  const visibleLines = partLines.filter(l => !l._deleted);
  const deletedLines = partLines.filter(l => l._deleted);

  return (
    <div className="p-6 max-w-[1600px] mx-auto animate-fade-in" data-testid="master-mould-parts">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <Link2 className="w-6 h-6 text-sky-500" />
            Mould-Part Mapping
          </h1>
          <p className="text-slate-500 text-sm">
            {Object.keys(groupedData).length} moulds, {data.length} total mappings
          </p>
        </div>
        {canEdit && (
          <Button
            onClick={handleAddNew}
            className="bg-slate-900 hover:bg-slate-800 rounded-sm"
            data-testid="add-new-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Mapping
          </Button>
        )}
      </div>

      {/* Search */}
      <Card className="mb-4 border-slate-200">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by mould or part..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 rounded-sm"
                data-testid="search-input"
              />
            </div>
            <Button variant="outline" size="icon" onClick={fetchData} className="rounded-sm">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Grouped Data Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="spinner" />
            </div>
          ) : filteredMouldIds.length === 0 ? (
            <div className="text-center py-12 text-slate-500">No records found</div>
          ) : (
            <div className="overflow-x-auto">
              {filteredMouldIds.map(mouldId => {
                const mouldParts = groupedData[mouldId];
                const isExpanded = expandedMoulds[mouldId];
                return (
                  <div key={mouldId} className="border-b border-slate-100 last:border-b-0" data-testid={`mould-group-${mouldId}`}>
                    {/* Mould Header Row */}
                    <div
                      className="flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 cursor-pointer transition-colors"
                      onClick={() => toggleMould(mouldId)}
                    >
                      <div className="flex items-center gap-3">
                        {isExpanded ? (
                          <ChevronDown className="w-4 h-4 text-slate-500" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-slate-500" />
                        )}
                        <span className="font-semibold text-slate-800">{getMouldName(mouldId)}</span>
                        <span className="text-xs bg-sky-100 text-sky-700 px-2 py-0.5 rounded-full font-medium">
                          {mouldParts.length} part{mouldParts.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                      {canEdit && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); handleEditMould(mouldId); }}
                          className="rounded-sm text-xs h-7"
                          data-testid={`edit-mould-${mouldId}`}
                        >
                          <Pencil className="w-3 h-3 mr-1" />
                          Edit Parts
                        </Button>
                      )}
                    </div>
                    {/* Parts Sub-table */}
                    {isExpanded && (
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-white hover:bg-white">
                            <TableHead className="pl-12 w-[40%]">Part</TableHead>
                            <TableHead>Total Cavities</TableHead>
                            <TableHead>Remarks</TableHead>
                            <TableHead>Status</TableHead>
                            {canEdit && <TableHead className="text-right">Actions</TableHead>}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {mouldParts.map(item => (
                            <TableRow key={item.id} className="hover:bg-slate-50">
                              <TableCell className="pl-12 font-medium">{getPartName(item.part_id)}</TableCell>
                              <TableCell className="font-mono">{item.total_cavities}</TableCell>
                              <TableCell className="text-sm text-slate-600">{item.remarks || '-'}</TableCell>
                              <TableCell>
                                {item.is_active ? (
                                  <span className="badge-success">Active</span>
                                ) : (
                                  <span className="badge-danger">Inactive</span>
                                )}
                              </TableCell>
                              {canEdit && (
                                <TableCell className="text-right">
                                  <Button variant="ghost" size="icon" onClick={() => handleDeleteMapping(item)} className="h-7 w-7 text-red-600">
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </Button>
                                </TableCell>
                              )}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog - Multi-part for a mould */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {isNewMould ? 'Add Mould-Part Mappings' : `Edit Parts - ${getMouldName(parseInt(selectedMouldId))}`}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            {/* Mould selector (only for new) */}
            {isNewMould && (
              <div className="mb-4">
                <Label className="form-label">Mould <span className="text-red-500">*</span></Label>
                <Select value={selectedMouldId} onValueChange={setSelectedMouldId}>
                  <SelectTrigger className="rounded-sm"><SelectValue placeholder="Select Mould" /></SelectTrigger>
                  <SelectContent>
                    {moulds.filter(m => m.is_active).map(m => (
                      <SelectItem key={m.id} value={m.id.toString()}>{m.code} - {m.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Parts table */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold">Parts ({visibleLines.length})</h3>
                <Button type="button" variant="outline" size="sm" onClick={handleAddPartLine} className="rounded-sm">
                  <Plus className="w-3 h-3 mr-1" /> Add Part
                </Button>
              </div>
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="w-[45%]">Part</TableHead>
                    <TableHead>Cavities</TableHead>
                    <TableHead>Remarks</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {partLines.map((line, i) => {
                    if (line._deleted) return null;
                    return (
                      <TableRow key={i}>
                        <TableCell>
                          <Select value={line.part_id} onValueChange={(v) => handlePartLineChange(i, 'part_id', v)}>
                            <SelectTrigger className="rounded-sm"><SelectValue placeholder="Select Part" /></SelectTrigger>
                            <SelectContent>
                              {parts.filter(p => p.status === 'ACTIVE' || p.is_active).map(p => (
                                <SelectItem key={p.id} value={p.id.toString()}>{p.part_no} - {p.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            value={line.total_cavities}
                            onChange={(e) => handlePartLineChange(i, 'total_cavities', e.target.value)}
                            className="rounded-sm w-20"
                            placeholder="4"
                            required
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            value={line.remarks || ''}
                            onChange={(e) => handlePartLineChange(i, 'remarks', e.target.value)}
                            className="rounded-sm"
                            placeholder="Optional"
                          />
                        </TableCell>
                        <TableCell>
                          <Button type="button" variant="ghost" size="icon" onClick={() => handleRemovePartLine(i)} className="text-red-600 h-7 w-7">
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {visibleLines.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-slate-400 py-4">
                        No parts. Click "+ Add Part" to add.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              {/* Deleted lines info */}
              {deletedLines.length > 0 && (
                <div className="mt-2 p-2 bg-red-50 rounded text-sm text-red-700">
                  {deletedLines.length} part{deletedLines.length !== 1 ? 's' : ''} will be removed on save.
                  {deletedLines.map((line, idx) => {
                    const origIdx = partLines.indexOf(line);
                    return (
                      <span key={idx} className="ml-2">
                        <button type="button" className="underline text-red-800 hover:text-red-600" onClick={() => handleUndoRemove(origIdx)}>
                          Undo {getPartName(parseInt(line.part_id))}
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}
            </div>

            <DialogFooter className="mt-4">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="rounded-sm">Cancel</Button>
              <Button type="submit" disabled={isSubmitting} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="submit-form">
                {isSubmitting ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MouldParts;
